import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Progress, Space, Typography, Button, Tag, message } from "antd";
import { CloudSyncOutlined, CheckCircleTwoTone, WarningTwoTone,
         ClockCircleOutlined, MinusOutlined, ExpandAltOutlined } from "@ant-design/icons";
import { getMaintenanceStatus, approveMaintenance, deferMaintenance } from "../api/client";
import { flushAllDrafts } from "../utils/draft";
import { useAuth } from "../context/AuthContext";
import { faNum } from "../utils/faNumberFormat";

const { Text } = Typography;

/**
 * اعلانِ «سیستم دارد بروزرسانی می‌شود» برای هر کاربری که همان لحظه داخلِ
 * برنامه است.
 *
 * چرا لازم است: بروزرسانی وسطِ کارِ کاربر، فرمِ نیمه‌کارهٔ او را بی‌صدا از بین
 * می‌برد. اینجا اول شمارشِ معکوس می‌آید («۲۰ ثانیه فرصت داری ذخیره کنی»)، بعد
 * پیامِ «در حالِ بروزرسانی»، و آخر «تمام شد، ادامه بده».
 *
 * نکتهٔ اصلیِ طراحی: در همان لحظه‌ای که بک‌اند ری‌استارت می‌شود، پُل کردن شکست
 * می‌خورد. اگر با هر خطا اعلان را پاک می‌کردیم، دقیقاً وقتی که کاربر بیشتر از
 * همیشه به آن نیاز دارد ناپدید می‌شد. پس بعد از دیدنِ اعلان، **خطای شبکه خودش
 * نشانهٔ ادامهٔ بروزرسانی است** و تا برگشتنِ سرور پیام سرِ جایش می‌ماند.
 */
/* ۱۰ ثانیه: باید از مهلتِ ذخیره (پیش‌فرض ۲۰ ثانیه) کوتاه‌تر باشد، وگرنه کاربر
   کلِ شمارشِ معکوس را از دست می‌دهد و اولین چیزی که می‌بیند «در حالِ
   بروزرسانی» است — یعنی دقیقاً همان فرصتی که برایش ساخته شد. */
const POLL_IDLE = 10000;
const POLL_HOT = 3000;      // وقتی اعلان فعال است، چسبیده

/* پیامِ «تمام شد» تا دو دقیقه روی سرور می‌ماند. بدونِ این نشانه، دکمهٔ
   «بارگذاری دوباره» صفحه را نو می‌کرد و همان پیام دوباره بالا می‌آمد — یک
   حلقهٔ بی‌پایان. پس هر اعلانی که کاربر دیده و بسته، با شناسه‌اش ثبت می‌شود. */
const SEEN_KEY = "maintenance-seen-id";
const seen = () => { try { return globalThis.localStorage?.getItem(SEEN_KEY); } catch { return null; } };
const markSeen = (id) => { try { globalThis.localStorage?.setItem(SEEN_KEY, id || "1"); } catch { /* noop */ } };

export default function MaintenanceWatcher() {
  const { isAuthed } = useAuth();
  const [st, setSt] = useState(null);
  /* v2.152.7 (کاربر ۱۴۰۵/۰۸/۰۲): «شاید با صفحهٔ پشتش کار داشته باشیم — بشود کوچکش کرد که مزاحم نشود و دوباره بازش کرد؛
     حالتِ کوچکش هم کاربردی باشد». کوچک ⇒ نوارِ گوشهٔ صفحه با وضعیت/شمارش و دکمه‌های اصلی؛ تا خودِ کاربر باز نکند کوچک می‌ماند. */
  const [mini, setMini] = useState(false);
  const activeRef = useRef(false);      // آیا اعلانی دیده‌ایم؟
  const timerRef = useRef(null);

  const tick = useCallback(async () => {
    if (!isAuthed) return;
    try {
      const r = await getMaintenanceStatus();
      const phase = r?.data?.phase;
      if (phase && phase !== "idle") {
        // اعلانِ پایانی که قبلاً دیده و بسته شده، دوباره نشان داده نمی‌شود
        if (phase === "done" && r.data.id && seen() === String(r.data.id)) {
          activeRef.current = false;
          setSt(null);
          return;
        }
        activeRef.current = true;
        setSt(r.data);
        // v2.142.0 — بروزرسانی در راه است ⇒ هر فرمِ نیمه‌کاره (تولید/برش/ضایعات) همین حالا پیش‌نویس شود
        if (phase !== "done") flushAllDrafts();
      } else if (activeRef.current) {
        // سرور برگشت و می‌گوید تمام است — پیامِ پایان را خودمان می‌سازیم
        activeRef.current = false;
        setSt({ phase: "done" });
      } else {
        setSt(null);
      }
    } catch {
      // سرور در دسترس نیست: اگر قبلاً اعلان دیده‌ایم یعنی همان بروزرسانی است
      if (activeRef.current) setSt((p) => ({ ...(p || {}), phase: "in_progress" }));
    }
  }, [isAuthed]);

  useEffect(() => {
    if (!isAuthed) return undefined;
    tick();
    const schedule = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        await tick();
        schedule();
      }, activeRef.current ? POLL_HOT : POLL_IDLE);
    };
    schedule();
    return () => clearTimeout(timerRef.current);
  }, [isAuthed, tick]);

  /* اگر کاربر وسطِ بروزرسانی چیزی ثبت کند و ۵۰۳ بگیرد، منتظرِ پُلِ بعدی
     نمی‌مانیم — همان لحظه وضعیت را می‌خوانیم تا بفهمد چرا ذخیره نشد. */
  useEffect(() => {
    const onBlocked = () => { activeRef.current = true; tick(); };
    window.addEventListener("gx-maintenance", onBlocked);
    return () => window.removeEventListener("gx-maintenance", onBlocked);
  }, [tick]);

  // پیامِ «تمام شد» بعد از چند ثانیه خودش می‌رود کنار
  useEffect(() => {
    if (st?.phase !== "done") return undefined;
    const id = st.id;
    const t = setTimeout(() => { markSeen(id); setSt(null); }, 20000);
    return () => clearTimeout(t);
  }, [st?.phase, st?.id]);

  const act = async (fn, okMsg) => {
    try {
      const r = await fn();
      if (r?.data?.ok === false) {
        message.warning(r.data.reason === "no_budget"
          ? "بیش از این نمی‌شود عقب انداخت — بروزرسانی باید انجام شود."
          : "دیگر درخواستِ بازی وجود ندارد.");
      } else if (okMsg) message.success(okMsg);
    } catch { message.error("انجام نشد — دوباره تلاش کنید"); }
    tick();
  };

  if (!st || st.phase === "idle") return null;

  const minBtn = (
    <Button size="small" type="text" icon={<MinusOutlined />} title="کوچک کردن"
      onClick={() => setMini(true)} style={{ position: "absolute", top: 8, insetInlineEnd: 8 }} />
  );

  /* ── حالتِ کوچک: یک نوارِ جمع‌وجور، بدونِ ماسک؛ صفحه کاملاً قابلِ کار است ── */
  if (mini) {
    const ph = st.phase;
    const failed = ph === "done" && st.result === "failed";
    const secs = ph === "pending" || ph === "scheduled" ? st.seconds_until_start : st.seconds_remaining;
    const clock = secs != null
      ? (secs >= 60 ? `${faNum(Math.floor(secs / 60))}:${String(secs % 60).padStart(2, "0").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d])}` : `${faNum(secs)} ث`)
      : null;
    const label = ph === "pending" ? "بروزرسانی آماده است"
      : ph === "scheduled" ? "بروزرسانی به‌زودی — کارتان را ذخیره کنید"
        : ph === "done" ? (failed ? "بروزرسانی انجام نشد" : "بروزرسانی انجام شد")
          : "در حالِ بروزرسانی — اطلاعات وارد نکنید";
    const color = ph === "done" ? (failed ? "#faad14" : "#52c41a")
      : ph === "pending" ? "#1677ff" : "#fa541c";
    return (
      <div style={{
        position: "fixed", insetInlineStart: 16, bottom: 16, zIndex: 1050,
        display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
        background: "var(--surface, #fff)", color: "var(--text, #222)",
        border: `1px solid ${color}`, borderRadius: 999, boxShadow: "0 6px 20px rgba(0,0,0,.15)",
        fontSize: 12, maxWidth: "calc(100vw - 32px)", flexWrap: "wrap",
      }}>
        {ph === "done"
          ? (failed ? <WarningTwoTone twoToneColor="#faad14" /> : <CheckCircleTwoTone twoToneColor="#52c41a" />)
          : <CloudSyncOutlined spin={ph === "in_progress"} style={{ color }} />}
        <b>{label}</b>
        {clock && ph !== "done" ? <Tag color="default" style={{ margin: 0 }}>{clock}</Tag> : null}
        {ph === "pending" && (
          <>
            <Button size="small" type="link" style={{ padding: 0 }}
              onClick={() => act(approveMaintenance, "همین حالا انجام می‌شود")}>همین حالا</Button>
            {st.can_defer && (
              <Button size="small" type="link" style={{ padding: 0 }}
                onClick={() => act(deferMaintenance,
                  `${faNum(Math.round((st.delay_seconds || 300) / 60))} دقیقه عقب افتاد`)}>
                {faNum(Math.round((st.delay_seconds || 300) / 60))} دقیقه دیگر
              </Button>)}
          </>
        )}
        {ph === "done" && !failed && (
          <Button size="small" type="link" style={{ padding: 0 }}
            onClick={() => { markSeen(st.id); window.location.reload(); }}>بارگذاریِ دوباره</Button>
        )}
        <Button size="small" type="text" icon={<ExpandAltOutlined />} title="باز کردن"
          onClick={() => setMini(false)} />
      </div>
    );
  }

  /* ── اجازه گرفتن ──
     عمداً **مودال نیست**: کارِ کاربر را قطع نمی‌کند، گوشهٔ صفحه می‌نشیند و
     منتظر می‌ماند. قبلاً بروزرسانی بی‌خبر شروع می‌شد و دسترسی یک‌دفعه قطع
     می‌شد؛ حالا بسته از قبل روی سرور نشسته و فقط منتظرِ لحظهٔ مناسب است. */
  if (st.phase === "pending") {
    const left = st.seconds_until_start ?? 0;
    const mins = Math.floor(left / 60);
    const when = mins > 0
      ? `${faNum(mins)} دقیقه و ${faNum(left % 60)} ثانیه`
      : `${faNum(left)} ثانیه`;
    return (
      <div style={{
        position: "fixed", insetInlineStart: 16, bottom: 16, zIndex: 1050,
        maxWidth: 380, background: "var(--surface, #fff)", color: "var(--text, #222)",
        border: "1px solid var(--border, #e4e8f0)", borderRadius: 12,
        boxShadow: "0 8px 30px rgba(0,0,0,.16)", padding: "12px 14px",
      }}>
        {minBtn}
        <Space direction="vertical" size={6} style={{ width: "100%" }}>
          <Space size={8}>
            <CloudSyncOutlined style={{ color: "#1677ff", fontSize: 20 }} />
            <b>بروزرسانیِ سیستم آماده است</b>
            {st.version_to ? <Tag color="blue">{st.version_to}</Tag> : null}
          </Space>
          <Text style={{ fontSize: 13 }}>
            فایل‌ها از قبل روی سرور آماده شده‌اند و نصب حدوداً
            {" "}{faNum(st.duration_seconds || 60)} ثانیه طول می‌کشد.
            اگر الان وقتش نیست، عقب بیندازید.
          </Text>
          {st.message ? <Text type="secondary" style={{ fontSize: 12 }}>{st.message}</Text> : null}
          <Text style={{ fontSize: 12, color: "#107C41" }}>
            ✓ برگه‌های نیمه‌کارهٔ برش/دوخت/ضایعات خودکار پیش‌نویس می‌شوند؛ بعد از بروزرسانی همان صفحه «ادامه» می‌دهد.
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <ClockCircleOutlined /> اگر کاری نکنید، تا {when} دیگر خودش انجام می‌شود.
            {st.deferred_by ? " (یک‌بار عقب افتاده)" : ""}
          </Text>
          <Space wrap>
            <Button type="primary" size="small"
              onClick={() => act(approveMaintenance, "همین حالا انجام می‌شود")}>
              همین حالا انجام بده
            </Button>
            <Button size="small" disabled={!st.can_defer}
              onClick={() => act(deferMaintenance,
                `${faNum(Math.round((st.delay_seconds || 300) / 60))} دقیقه عقب افتاد`)}>
              {st.can_defer
                ? <>{faNum(Math.round((st.delay_seconds || 300) / 60))} دقیقه دیگر</>
                : <>مهلتِ تعویق تمام شد</>}
            </Button>
            {st.max_delays > 0 && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                تعویق: {faNum(st.delays_used || 0)} از {faNum(st.max_delays)}
              </Text>
            )}
          </Space>
        </Space>
      </div>
    );
  }

  if (st.phase === "done") {
    const failed = st.result === "failed";
    const dismiss = () => { markSeen(st.id); setSt(null); };
    return (
      <Modal open centered footer={null} closable onCancel={dismiss} width={430}>
        <Button size="small" type="text" icon={<MinusOutlined />} title="کوچک کردن"
          onClick={() => setMini(true)} style={{ position: "absolute", top: 12, insetInlineStart: 12 }} />
        <Space direction="vertical" size={8} style={{ textAlign: "center", width: "100%" }}>
          {failed
            ? <WarningTwoTone twoToneColor="#faad14" style={{ fontSize: 40 }} />
            : <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 40 }} />}
          <b style={{ fontSize: 16 }}>
            {failed ? "بروزرسانی انجام نشد" : "بروزرسانی با موفقیت انجام شد"}
          </b>
          <Text type="secondary">
            {failed
              ? "سیستم به نسخهٔ قبلی بازگشت. اطلاعات شما محفوظ است."
              : "می‌توانید کار خود را ادامه دهید."}
          </Text>
          {st.version && (
            <Tag color={failed ? "orange" : "green"}>
              {failed ? "نسخهٔ فعلی" : "نسخهٔ جدید"}: {st.version}
            </Tag>
          )}
          {/* اول «دیده شد» ثبت می‌شود، بعد رفرش — وگرنه صفحهٔ نو دوباره
              همین پیام را نشان می‌دهد و کاربر در حلقه می‌افتد. */}
          <Button type="primary"
            onClick={() => { markSeen(st.id); window.location.reload(); }}>
            بارگذاریِ دوبارهٔ صفحه
          </Button>
          <Button type="link" size="small" onClick={dismiss}>بعداً</Button>
        </Space>
      </Modal>
    );
  }

  const scheduled = st.phase === "scheduled";
  const left = scheduled ? st.seconds_until_start : st.seconds_remaining;
  const total = scheduled
    ? Math.max(1, st.seconds_until_start || 1)
    : Math.max(1, st.duration_seconds || 1);
  const pct = Math.min(100, Math.round(((total - (left ?? 0)) / total) * 100));

  return (
    // بدونِ closable و بدونِ maskClosable: تا وقتی تمام نشده نباید بی‌خبر رد شود — ولی «کوچک کردن» دارد (نوارِ گوشه)
    <Modal open centered footer={null} closable={false} maskClosable={false} width={440}>
      {minBtn}
      <Space direction="vertical" size={10} style={{ textAlign: "center", width: "100%" }}>
        <CloudSyncOutlined style={{ fontSize: 38, color: "#1677ff" }} />
        <b style={{ fontSize: 16 }}>
          {scheduled
            ? <>سیستم تا {faNum(left ?? 0)} ثانیهٔ دیگر بروزرسانی می‌شود</>
            : <>در حالِ بروزرسانیِ سیستم…</>}
        </b>
        <Text>
          {scheduled
            ? "لطفاً کارِ نیمه‌کاره‌تان را همین حالا ذخیره کنید و اطلاعاتِ جدید وارد نکنید."
            : "لطفاً اطلاعاتی وارد نکنید. به‌محضِ آماده‌شدن، همین‌جا خبرتان می‌کنیم."}
        </Text>
        {st.message ? <Text type="secondary">{st.message}</Text> : null}
        <Progress percent={pct} showInfo={false} status="active" />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {scheduled
            ? <>شروع تا {faNum(left ?? 0)} ثانیهٔ دیگر</>
            : (left != null
              ? <>حدوداً {faNum(left)} ثانیه تا پایان</>
              : <>در حالِ اتصالِ دوباره به سرور…</>)}
        </Text>
      </Space>
    </Modal>
  );
}
